import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';

const TenantPaymentSettings = () => {
  const [paymentPreference, setPaymentPreference] = useState('tenant_specific');
  const [gateways, setGateways] = useState({
    payu: { config: {}, isActive: false },
    icici: { config: {}, isActive: false },
    idfc: { config: {}, isActive: false },
    phonepe: { config: {}, isActive: false }
  });
  const [saving, setSaving] = useState(false);
  const [activeGateway, setActiveGateway] = useState('payu');

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  const fetchPaymentSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || 'sevenwonders';
      
      const response = await axios.get(`${API_BASE_URL}/api/tenants/current`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId
        }
      });
      
      const tenant = response.data.tenant;
      setPaymentPreference(tenant.paymentPreference?.type || 'tenant_specific');
      
      // Load existing gateway configs
      const gatewayConfigs = {};
      tenant.paymentGateways?.forEach(pg => {
        gatewayConfigs[pg.provider] = {
          config: pg.config,
          isActive: pg.isActive
        };
      });
      setGateways(prev => ({ ...prev, ...gatewayConfigs }));
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const updatePaymentPreference = async (type) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || 'sevenwonders';
      
      await axios.put(`${API_BASE_URL}/api/tenants/payment-preference`, 
        { type },
        { headers: { 
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId
        } }
      );
      setPaymentPreference(type);
      alert('Payment preference updated successfully!');
    } catch (error) {
      console.error('Error updating preference:', error);
      alert('Error updating preference');
    } finally {
      setSaving(false);
    }
  };

  const saveGatewayConfig = async (gateway) => {
    try {
      // Validate PhonePe required fields
      if (gateway === 'phonepe') {
        const config = gateways[gateway].config;
        const requiredFields = ['merchantId', 'saltKey'];
        const missingFields = requiredFields.filter(field => !config[field]);
        
        if (missingFields.length > 0) {
          alert(`Please fill all required PhonePe fields: ${missingFields.join(', ')}`);
          return;
        }
      }
      
      setSaving(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || 'sevenwonders';
      
      await axios.post(`${API_BASE_URL}/api/tenants/payment-gateway`,
        { gateway, config: gateways[gateway].config },
        { headers: { 
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId
        } }
      );
      
      setGateways(prev => ({
        ...prev,
        [gateway]: { ...prev[gateway], isActive: true }
      }));
      
      alert(`${gateway.toUpperCase()} gateway configured successfully!`);
    } catch (error) {
      console.error('Error saving gateway:', error);
      alert(error.response?.data?.message || 'Error saving gateway configuration');
    } finally {
      setSaving(false);
    }
  };

  const gatewayFields = {
    payu: [
      { key: 'merchantKey', label: 'Merchant Key', type: 'text' },
      { key: 'merchantSalt', label: 'Merchant Salt', type: 'password' }
    ],
    icici: [
      { key: 'merchantId', label: 'Merchant ID', type: 'text' },
      { key: 'accessCode', label: 'Access Code', type: 'text' },
      { key: 'workingKey', label: 'Working Key', type: 'password' }
    ],
    idfc: [
      { key: 'merchantId', label: 'Merchant ID', type: 'text' },
      { key: 'secretKey', label: 'Secret Key', type: 'password' }
    ],
    phonepe: [
      { key: 'merchantId', label: 'Merchant ID', type: 'text' },
      { key: 'saltKey', label: 'Salt Key', type: 'password' },
      { key: 'saltIndex', label: 'Salt Index', type: 'text' },
      { key: 'environment', label: 'Environment', type: 'select', options: [
        { value: 'uat', label: 'UAT (Testing)' },
        { value: 'production', label: 'Production (Live)' }
      ]}
    ]
  };

  return (
    <TenantLayout userRole="tenant_admin">
      <header className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white px-8 py-8 shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2">Payment Gateway Settings</h1>
            <p className="text-blue-100">Configure payment gateways for your bookings</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-8 overflow-auto">
        <div className="space-y-8">
          
          {/* Payment Preference */}
          <div className="bg-white rounded-2xl shadow-xl border p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Gateway Preference</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div 
                onClick={() => updatePaymentPreference('tenant_specific')}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentPreference === 'tenant_specific' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center mb-4">
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                    paymentPreference === 'tenant_specific' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                  }`}></div>
                  <h3 className="text-lg font-semibold">🏢 Tenant-Specific Gateways</h3>
                </div>
                <p className="text-gray-600">Use your own payment gateway credentials. You have full control over payment processing and settlements.</p>
                <div className="mt-4 text-sm text-green-600 font-medium">
                  ✓ Direct settlements ✓ Custom rates ✓ Full control
                </div>
              </div>

              <div 
                onClick={() => updatePaymentPreference('central')}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentPreference === 'central' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center mb-4">
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                    paymentPreference === 'central' ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                  }`}></div>
                  <h3 className="text-lg font-semibold">🏛️ Central System Gateways</h3>
                </div>
                <p className="text-gray-600">Use shared payment gateways managed by the system. Quick setup with no configuration required.</p>
                <div className="mt-4 text-sm text-blue-600 font-medium">
                  ✓ Instant setup ✓ No configuration ✓ Managed by system
                </div>
              </div>
            </div>
          </div>

          {/* Gateway Configuration (only show if tenant_specific) */}
          {paymentPreference === 'tenant_specific' && (
            <div className="bg-white rounded-2xl shadow-xl border p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Configure Payment Gateways</h2>
              
              {/* Gateway Tabs */}
              <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 mb-8">
                {Object.keys(gateways).map(gateway => (
                  <button
                    key={gateway}
                    onClick={() => setActiveGateway(gateway)}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all ${
                      activeGateway === gateway
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <span>{gateway === 'payu' ? '💳' : gateway === 'icici' ? '🏦' : gateway === 'idfc' ? '🏛️' : '📱'}</span>
                    <span className="uppercase">{gateway}</span>
                    {gateways[gateway].isActive && <span className="text-green-400">✓</span>}
                  </button>
                ))}
              </div>

              {/* Gateway Configuration Form */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold capitalize">{activeGateway} Configuration</h3>
                  {gateways[activeGateway].isActive && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      ✓ Active
                    </span>
                  )}
                </div>

                <div className="space-y-6">
                  {/* PhonePe Help Text */}
                  {activeGateway === 'phonepe' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">📱 PhonePe Configuration</h4>
                      <p className="text-blue-700 text-sm mb-2">
                        Enter your PhonePe merchant credentials. Each tenant needs separate credentials from PhonePe.
                      </p>
                      <div className="text-xs text-blue-600">
                        <strong>Test Credentials Example:</strong><br/>
                        • Merchant ID: TRONIXUAT<br/>
                        • Salt Key: 099eb0cd-02cf-4e2a-8aca-3e6c6aff0399<br/>
                        • Salt Index: 1
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {gatewayFields[activeGateway]?.map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label}
                          {activeGateway === 'phonepe' && field.key === 'merchantId' && <span className="text-red-500">*</span>}
                          {activeGateway === 'phonepe' && field.key === 'saltKey' && <span className="text-red-500">*</span>}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={gateways[activeGateway].config[field.key] || (field.key === 'environment' ? 'uat' : '')}
                            onChange={(e) => setGateways(prev => ({
                              ...prev,
                              [activeGateway]: {
                                ...prev[activeGateway],
                                config: {
                                  ...prev[activeGateway].config,
                                  [field.key]: e.target.value
                                }
                              }
                            }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            {field.options?.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type}
                            value={gateways[activeGateway].config[field.key] || ''}
                            onChange={(e) => setGateways(prev => ({
                              ...prev,
                              [activeGateway]: {
                                ...prev[activeGateway],
                                config: {
                                  ...prev[activeGateway].config,
                                  [field.key]: e.target.value
                                }
                              }
                            }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder={
                              activeGateway === 'phonepe' && field.key === 'merchantId' ? 'e.g., TRONIXUAT' :
                              activeGateway === 'phonepe' && field.key === 'saltIndex' ? 'e.g., 1' :
                              `Enter ${field.label.toLowerCase()}`
                            }
                            required={activeGateway === 'phonepe' && ['merchantId', 'saltKey'].includes(field.key)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => saveGatewayConfig(activeGateway)}
                    disabled={saving}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : `Save ${activeGateway.toUpperCase()} Config`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Central Gateway Info */}
          {paymentPreference === 'central' && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl border border-green-200 p-8">
              <div className="text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">You're all set!</h3>
                <p className="text-gray-600 mb-6">
                  You're using central payment gateways managed by the system. 
                  No configuration required - payments will be processed automatically.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['PayU', 'ICICI', 'IDFC', 'PhonePe'].map(gateway => (
                    <div key={gateway} className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl mb-2">
                        {gateway === 'PayU' ? '💳' : gateway === 'ICICI' ? '🏦' : gateway === 'IDFC' ? '🏛️' : '📱'}
                      </div>
                      <div className="font-medium">{gateway}</div>
                      <div className="text-sm text-green-600">✓ Available</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </TenantLayout>
  );
};

export default TenantPaymentSettings;